import React, {useEffect, useMemo, useState} from 'react';
import {
    Box,
    CircularProgress,
    Button,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Chip,
} from '@mui/material';
import {InfoOutlined} from '@mui/icons-material';
import {useTheme} from '@mui/material/styles';
import {Menu, queryMenuTree, deleteMenu} from '@/services/manageService/menuService';
import {SnackbarField, PopoverButton} from '@/components/common';
import {usePageCrud} from '@/hooks/usePageCrud';
import {
    flattenTree,
    getVisibleTreeRows,
    treeLeadingCellSx,
    treeLevelRowBg,
} from '@/utils/treeTableHelpers';
import {TreeExpandCell} from '@/utils/TreeExpandCell';
import MenuDetail from '@/pages/components/manage/sys/menu/MenuDetail';

// menuType 显示映射
function renderMenuType(t: number): string {
    switch (t) {
        case 1:
            return 'content';
        case 2:
            return 'menu';
        case 3:
            return 'button';
        case 4:
            return 'special';
        default:
            return String(t ?? '');
    }
}

function renderMenuTypeChip(t: number) {
    const label = renderMenuType(t);
    let color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' = 'default';
    switch (t) {
        case 1:
            color = 'primary'; // content
            break;
        case 2:
            color = 'secondary'; // menu
            break;
        case 3:
            color = 'warning'; // button
            break;
        case 4:
            color = 'info'; // special
            break;
        default:
            color = 'default';
    }
    return <Chip label={label} size="small" color={color} variant="outlined"/>;
}

// 将扁平数据按 pid 组装为树（若后端已返回 children，则直接使用返回的数据）
function buildTree(list: Menu[]): Menu[] {
    if (!Array.isArray(list)) return [];
    // 如果任一元素存在 children 字段且为数组，认为已是树
    const alreadyTree = list.some((n) => Array.isArray((n as any).children) && (n as any).children.length >= 0);
    if (alreadyTree) return list;

    const idMap = new Map<number, Menu & { children: Menu[] }>();
    list.forEach((item) => {
        idMap.set(item.menuId, {...item, children: []});
    });
    const roots: (Menu & { children: Menu[] })[] = [];
    list.forEach((item) => {
        const node = idMap.get(item.menuId)!;
        if (item.pid && idMap.has(item.pid)) {
            idMap.get(item.pid)!.children.push(node);
        } else {
            roots.push(node);
        }
    });
    return roots;
}

const MenuList: React.FC = () => {
    const theme = useTheme();
    const [tree, setTree] = useState<Menu[] | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [openRowIds, setOpenRowIds] = useState<number[]>([]);
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: 'success' | 'error';
    }>({open: false, message: '', severity: 'success'});
    const [detailOpen, setDetailOpen] = useState(false);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [isAdd, setIsAdd] = useState<boolean>(false);
    const {canRead, canCreate, canRemove} = usePageCrud({
        read: ['admin_menu_query', 'admin_menu_detail', 'admin_menu_tree'],
        create: 'admin:menu:create',
        update: 'admin:menu:update',
        remove: 'admin:menu:delete',
    });

    useEffect(() => {
        setLoading(true);
        queryMenuTree({pid: 0})
            .then((res) => {
                const t = buildTree(res || []);
                setTree(t);
                setError(null);
            })
            .catch((e) => {
                setError(e?.message || 'Load failed');
                setTree([]);
            })
            .finally(() => setLoading(false));
    }, []);

    const refreshList = () => {
        setLoading(true);
        queryMenuTree({pid: 0})
            .then((res) => {
                const t = buildTree(res || []);
                setTree(t);
                setError(null);
            })
            .catch((e) => {
                setError(e?.message || 'Load failed');
                setTree([]);
            })
            .finally(() => setLoading(false));
    };

    const rows = useMemo(() => flattenTree(tree || [], (n) => n.menuId), [tree]);
    const visibleRows = useMemo(() => getVisibleTreeRows(rows, openRowIds), [rows, openRowIds]);

    const handleToggleRow = (id: number) => {
        setOpenRowIds((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
    };

    const handleDetails = (menuId: number) => {
        setSelectedId(menuId);
        setIsAdd(false);
        setDetailOpen(true);
    };

    const handleAdd = () => {
        setSelectedId(null);
        setIsAdd(true);
        setDetailOpen(true);
    };

    const handleDetailClose = (result?: { type: 'create' | 'update'; data?: any }) => {
        setDetailOpen(false);
        if (result?.type === 'create' && result.data) {
            setSnackbar({open: true, message: `${result.data.menuName} Create Successful!`, severity: 'success'});
            refreshList();
        } else if (result?.type === 'update') {
            setSnackbar({open: true, message: 'Update Successful!', severity: 'success'});
            refreshList();
        }
    };

    const handleRemove = async (menuId: number, menuName?: string) => {
        try {
            await deleteMenu(menuId);
            setSnackbar({open: true, message: 'Remove Successful!', severity: 'success'});
            refreshList();
        } catch (e: any) {
            const msg = e?.message || '删除失败';
            setSnackbar({open: true, message: msg, severity: 'error'});
        }
    };

    if (loading) return <CircularProgress sx={{display: 'block', mx: 'auto', my: 4}}/>;
    if (error) return <Typography color="error">{error}</Typography>;
    if (!tree || tree.length === 0) return <Typography>No Data Yet</Typography>;

    return (
        <Box sx={{
            p: 2,
            bgcolor: 'transparent',
            height: 'calc(100vh - 120px)',
            minHeight: '70vh',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
        }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} flexShrink={0}>
                <Typography variant="h6" fontWeight={700} color="text.primary">
                    Menu List
                </Typography>
                {canCreate && (
                <Button
                    variant="contained"
                    color="secondary"
                    sx={{
                        borderRadius: theme.shape.borderRadius,
                        textTransform: 'none',
                        fontWeight: 600,
                    }}
                    onClick={handleAdd}
                >
                    ADD
                </Button>
                )}
            </Stack>
            <TableContainer
                component={Paper}
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflow: 'auto',
                    boxShadow: theme.shadows[2],
                    bgcolor: 'background.paper',
                }}
            >
                <Table
                    size="small"
                    stickyHeader
                    sx={{
                        minWidth: 650,
                        tableLayout: 'fixed',
                        '& .MuiTableCell-stickyHeader': {
                            bgcolor: 'background.paper',
                            zIndex: 3,
                            borderBottom: `1px solid ${theme.palette.divider}`,
                        },
                    }}
                >
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{width: 48, p: 0}}></TableCell>
                            <TableCell sx={{width: 180, fontWeight: 700}}>Menu Name</TableCell>
                            <TableCell sx={{width: 180, fontWeight: 700}}>Real Url</TableCell>
                            <TableCell sx={{width: 120, fontWeight: 700}}>Menu Type</TableCell>
                            <TableCell sx={{width: 200, fontWeight: 700}}>Button Permission</TableCell>
                            <TableCell sx={{width: 160, fontWeight: 700}}>Menu Icon</TableCell>
                            <TableCell sx={{width: 220, fontWeight: 700}} align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {visibleRows.map((row) => (
                            <TableRow
                                key={row.menuId}
                                hover
                                sx={{bgcolor: treeLevelRowBg(row.level, theme)}}
                            >
                                <TableCell sx={{width: 48, p: 0.5, ...treeLeadingCellSx(row.level)}}>
                                    <TreeExpandCell
                                        hasChildren={!!(row.children && row.children.length > 0)}
                                        isOpen={openRowIds.includes(row.menuId)}
                                        onToggle={() => handleToggleRow(row.menuId)}
                                    />
                                </TableCell>
                                <TableCell sx={{
                                    width: 180,
                                    fontWeight: 500,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    ...treeLeadingCellSx(row.level),
                                }}>{row.menuName}</TableCell>
                                <TableCell sx={{
                                    width: 320,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                }}>{row.realmUrl}</TableCell>
                                <TableCell sx={{width: 120}}>{renderMenuTypeChip(row.menuType)}</TableCell>
                                <TableCell sx={{width: 120}}>{row.realm}</TableCell>
                                <TableCell sx={{
                                    width: 160,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                }}>{row.menuIcon}</TableCell>
                                <TableCell sx={{width: 220}} align="center">
                                    <Stack direction="row" spacing={1} justifyContent="center">
                                        {canRead && (
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            sx={{
                                                minWidth: 80,
                                                textTransform: 'none',
                                                borderColor: theme.palette.secondary.main,
                                                color: theme.palette.secondary.main,
                                                fontWeight: 600,
                                                '&:hover': {
                                                    borderColor: theme.palette.secondary.dark,
                                                    color: theme.palette.secondary.dark,
                                                },
                                            }}
                                            startIcon={<InfoOutlined/>}
                                            onClick={() => handleDetails(row.menuId)}
                                        >
                                            Details
                                        </Button>
                                        )}
                                        {canRemove && (
                                        <PopoverButton
                                            nodeId={row.menuId}
                                            nodeName={row.menuName}
                                            onRemove={(id) => handleRemove(id)}
                                            theme={theme}
                                        />
                                        )}
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <SnackbarField
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                autoHideDuration={4000}
                onClose={() => setSnackbar({...snackbar, open: false})}
            />
            <MenuDetail
                open={detailOpen}
                isAdd={isAdd}
                menuId={selectedId ?? undefined}
                onClose={handleDetailClose}
            />
        </Box>
    );
};

export default MenuList;